import React, { useState } from 'react';
import EditDrawer from './EditDrawer';
import Field from '../../Field';
import {
  formatCredits, makeupCreditsError, makeupRefusalMessage, makeupExpiredLine, makeupCapHint, creditsAsSaved,
} from '../../../domain/makeup';
import s from './editStudent.module.css';
import { DecimalInput } from '../../RegionInputs';
import { useLocale } from '../../../context/LocaleContext';

// `Set Make-Up Credits` (#178).
//
// MMS's is the fifth row of the Lesson Settings drawer — a number field
// showing the balance, suffixed `Credits`, captioned `Optional`, saved by the
// same Save as the rate. Ours is its own drawer, opened from the Make-Up
// Credits block: a knowing divergence about the write. MMS's row edits a
// number on the student document; ours writes a different collection
// (data/useMakeup.jsx), on its own transaction, with a reason field, and
// folding it into `EditLessonSettingsDrawer` would put two unrelated writes
// behind one Save. The same shape as `FamilyContactDrawer`
// (StudentDetailPage.jsx): `useStudentEdit` covers one student-and-family
// pair's slices, and this is neither.
//
// The number is seeded with the student's current Total and read as the total
// they should end up with — absolute, not a delta (`setCredits` explains).
// The reason is ours: our credits are a ledger, and an unexplained entry is
// the untraceable figure #178 objected to. `Optional`, because refusing a
// grant until somebody explains themselves would be a worse control.
//
// It takes the fold and the writer rather than calling `useMakeup`: that
// provider reaches `src/lib/firebase.js`, which the `ui` jest project has no
// mapper for, so the refusals and the cap warning below stay testable there.
//
// The number box is seeded in `useState`, which runs once per mount, so the
// page renders this only while it is open; kept mounted, it would show a
// stale total after an absence was recorded on the calendar.

// `Optional` is MMS's caption on this field, kept verbatim.
const CREDITS_HINT = 'Optional — the total this student should have';

export default function SetMakeupCreditsDrawer({
  open = true, name = null, summary, policy = null, loaded = true,
  creditsMax, reasonMax, durationMin = null, onSave, onClose,
}) {
  // `?? 0` covers a missing summary and the `null` total of a student with no
  // lesson length. The latter cannot be saved — `setCredits` refuses it with
  // `no-duration` — and 0 is less misleading than `null` until it does.
  const [text, setText] = useState(String(summary?.total ?? 0));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  // Why the last Save did not happen, or null. See `save()` below.
  const [refusal, setRefusal] = useState(null);
  // The figures in the sentence, in the studio's Region: `1,5` (#549).
  const { decimalSeparator: decimal } = useLocale();

  if (!open) return null;

  // Refused at the field rather than discovered on Save (#115, #121, #164).
  // `setCredits` refuses the same values; both are given the bound.
  // Fractional (#352): a credit is a lesson-length of time, so `3.33` is a
  // real balance — MMS shows it for five 30-minute credits at a 45-minute
  // lesson. `setCredits` accepts any finite number in range and rounds the
  // minutes it comes to.
  const trimmed = text.trim();
  const value = trimmed === '' ? NaN : Number(trimmed);
  const error = makeupCreditsError(text, creditsMax);

  // Closing is what "it worked" looks like, so it closes only on `done`.
  // `setCredits` answers `{ done, written }` or `{ done, reason }` — signed
  // out, a ledger it has not read, a total outside the bound — and anything
  // but `done` keeps the drawer open and says why, so a teacher is never told
  // a balance moved when it did not. Fail-closed on an unrecognised answer.
  const save = async () => {
    if (error || !loaded) return;
    setSaving(true);
    setRefusal(null);
    try {
      const result = await onSave({ total: value, reason });
      if (result?.done) {
        onClose?.();
        return;
      }
      setRefusal(result?.reason || 'unknown');
    } finally {
      setSaving(false);
    }
  };

  // One message per reason `setCredits` can give: three of the five are
  // things the teacher can act on, and `no-duration` names a different field.
  // The sentences are `makeupRefusalMessage` in src/domain/makeup.js.
  // `no-duration` names the Duration field on Lesson Settings, because the
  // student's lesson length divides every credit number here (#352).

  return (
    <EditDrawer open title="Set Make-Up Credits" saving={saving} onClose={onClose} onSave={save}>
      <div className={s.hint}>
        {name ? `${name} has ` : 'This student has '}
        <b>{formatCredits(summary.total, decimal)}</b>
        {' in total, '}
        <b>{formatCredits(summary.booked, decimal)}</b>
        {' booked against a future lesson and '}
        <b>{formatCredits(summary.available, decimal)}</b>
        {' left to spend.'}
        {/* `creditSummary` folds expiry on read, so a lapsed credit is gone
            from Total with nothing else on screen to say it existed. */}
        {makeupExpiredLine(summary.expired, decimal)}
      </div>

      <div className={[s.spaced, s.narrow].join(' ')}>
        <Field
          label="Make-Up Credits" htmlFor="set-makeup-credits"
          hint={CREDITS_HINT}
          error={error}
        >
          {/* `step="0.01"` matches the two places MMS shows — `3.33`, `2.50` —
              and is advisory, as `step="5"` is on the Duration field: a value
              off the step is refused neither here nor in `setCredits`, which
              rounds the minutes instead. */}
          {/* MMS rounds this box to whole minutes and shows the rounded figure
              when focus leaves it, before Save (#586): `0.75` at a 30-minute
              lesson reads `0.77`. So this rewrites the text on blur to what
              Save would write, with `creditsAsSaved` (the core's
              `credits_as_saved`, the read-only twin of `plan_set_credits`'s
              rounding). A value the rule cannot round — empty, not a number, or
              no `durationMin` to divide by — is left as typed. */}
          <DecimalInput
            id="set-makeup-credits"
            className={s.input}
            value={text} onChange={(e) => setText(e.target.value)}
            onBlur={() => {
              const trimmedOnBlur = text.trim();
              if (trimmedOnBlur === '') return;
              const n = Number(trimmedOnBlur);
              if (!Number.isFinite(n)) return;
              const rounded = creditsAsSaved(n, durationMin);
              if (rounded != null) setText(String(rounded));
            }}
          />
        </Field>
      </div>

      {/* The cap is folded on read, so a total typed above it is written and
          then not shown, which looks like a failed save; the number is named.
          `maxCredits` is null for "no cap" and 0 for "this studio issues
          none", so the test is `!= null` (domain/makeup.js). */}
      {makeupCapHint(policy?.maxCredits, value) ? (
        <div className={s.hint}>{makeupCapHint(policy?.maxCredits, value)}</div>
      ) : null}

      <div className={s.spaced}>
        <Field
          label="Reason" htmlFor="set-makeup-reason"
          hint="Optional — filed with the credit, so the change has a history"
        >
          <textarea
            id="set-makeup-reason" rows={3} className={s.textarea}
            maxLength={reasonMax}
            value={reason} onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      </div>

      {/* A balance not yet read is one no delta can be measured against, so
          Save would write the wrong number. Said on screen rather than greying
          the button: Save is never disabled for a form reason (EditDrawer),
          and this is the data behind the form being absent. */}
      {loaded ? null : (
        <div className={s.hint}>
          Still reading this student’s credits — try again in a moment.
        </div>
      )}

      {/* The writer's own refusal after the press, so a refusal is never
          mistaken for a save: the drawer stays open and says so. */}
      {refusal ? (
        <div className={s.hint} role="alert">{makeupRefusalMessage(refusal, creditsMax)}</div>
      ) : null}
    </EditDrawer>
  );
}
